import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Form, InputGroup, Spinner } from "react-bootstrap";
import { 
  collection, addDoc, query, getDocs, 
  orderBy, Timestamp, updateDoc, deleteDoc, doc, limit 
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import { 
  FaPlus, FaTrash, FaEdit, FaClipboardList, 
  FaArchive, FaUndo, FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaShieldAlt 
} from 'react-icons/fa';

const ADMIN_PASSCODE = process.env.REACT_APP_ADMIN_PASSCODE || "1234";

const normalizeDateStr = (str) => {
  if (!str) return "";
  const parts = str.split('-');
  if (parts.length !== 3) return str;
  return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
};

const getActiveShiftDateStr = (date = new Date()) => {
  const d = new Date(date);
  if (d.getHours() < 2) d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
};

const isListActiveForCurrentShift = (list) => {
  if (!list || !list.eventDate || list.isArchived) return false;
  const now = new Date();
  const currentShiftStr = getActiveShiftDateStr(now);
  const isTodayShift = normalizeDateStr(list.eventDate) === normalizeDateStr(currentShiftStr);
  if (!isTodayShift) return false;

  if (list.expiryDate) {
    const expiry = list.expiryDate.toDate ? list.expiryDate.toDate() : new Date(list.expiryDate);
    if (expiry < now) return false;
  }
  return true;
};

const calculateShiftExpiry = (dateStr) => {
  const shiftDate = new Date(dateStr + "T00:00:00");
  const nextDay2AM = new Date(shiftDate);
  nextDay2AM.setDate(nextDay2AM.getDate() + 1);
  nextDay2AM.setHours(2, 0, 0, 0);
  return Timestamp.fromDate(nextDay2AM);
};

const EventListManager = React.memo(({ 
  show, 
  onHide, 
  isAdmin, 
  isBarAdmin,
  db, 
  formatDisplayValue, 
  logVisit, 
  verifyMemberProfile, 
  getStaffVerifierTag, 
  onCheckInSuccess 
}) => {
  const [guestLists, setGuestLists] = useState([]);
  const [selectedGuestList, setSelectedGuestList] = useState(null);
  const [tabMode, setTabMode] = useState('active');
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingListId, setEditingListId] = useState(null);
  const [singleGuestInput, setSingleGuestInput] = useState('');
  const [savingList, setSavingList] = useState(false);

  const [denialModal, setDenialModal] = useState({
    show: false,
    guestEntry: null,
    reason: '',
    isBanned: false,
    isFlagged: false,
    hasIncident: false,
    isExpired: false
  });

  const [listForm, setListForm] = useState({
    listName: '',
    sponsorName: '',
    eventDate: new Date().toISOString().split('T')[0],
    rawNames: ''
  });

  const fetchGuestLists = useCallback(async () => {
    try {
      const q = query(collection(db, "guest_lists"), orderBy("createdAt", "desc"), limit(100));
      const snap = await getDocs(q);
      const lists = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setGuestLists(lists);
    } catch (err) {
      console.error("Error fetching guest lists:", err);
      toast.error("Failed to load Event Lists");
    }
  }, [db]);

  useEffect(() => {
    if (show) {
      fetchGuestLists();
    }
  }, [show, fetchGuestLists]);

  const filteredGuestLists = useMemo(() => {
    return guestLists.filter(list => {
      if (tabMode === 'active') {
        return !list.isArchived && isListActiveForCurrentShift(list);
      }
      if (tabMode === 'admin') {
        return isAdmin;
      }
      if (tabMode === 'archived') {
        return !!list.isArchived;
      }
      return true;
    });
  }, [guestLists, tabMode, isAdmin]);

  useEffect(() => {
    if (filteredGuestLists.length > 0) {
      if (!selectedGuestList || !filteredGuestLists.some(l => l.id === selectedGuestList.id)) {
        setSelectedGuestList(filteredGuestLists[0]);
      } else {
        const updatedSelected = filteredGuestLists.find(l => l.id === selectedGuestList.id);
        if (updatedSelected) setSelectedGuestList(updatedSelected);
      }
    } else {
      setSelectedGuestList(null);
    }
  }, [filteredGuestLists]);

  const handleSaveGuestList = async () => {
    if (!listForm.listName.trim()) return toast.error("List Name is required");
    if (!listForm.sponsorName.trim()) return toast.error("Sponsor Name is required");

    setSavingList(true);
    try {
      const parsedEntries = listForm.rawNames
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0)
        .map((name, idx) => ({
          id: `entry_${Date.now()}_${idx}`,
          fullName: name,
          isVerified: false,
          verifiedBy: null,
          checkedInAt: null
        }));

      const expiryTimestamp = calculateShiftExpiry(listForm.eventDate);

      if (editingListId) {
        const listRef = doc(db, "guest_lists", editingListId);
        const existingList = guestLists.find(l => l.id === editingListId);
        
        const existingEntriesMap = new Map((existingList?.guestEntries || []).map(e => [e.fullName.toLowerCase(), e]));
        const mergedEntries = parsedEntries.map(entry => {
          const matched = existingEntriesMap.get(entry.fullName.toLowerCase());
          return matched ? { ...entry, isVerified: matched.isVerified, verifiedBy: matched.verifiedBy, checkedInAt: matched.checkedInAt } : entry;
        });

        const updatePayload = {
          listName: listForm.listName.trim(),
          sponsorName: listForm.sponsorName.trim(),
          eventDate: listForm.eventDate,
          expiryDate: expiryTimestamp,
          guestEntries: mergedEntries
        };

        await updateDoc(listRef, updatePayload);
        toast.success("Event List updated successfully!");
      } else {
        await addDoc(collection(db, "guest_lists"), {
          listName: listForm.listName.trim(),
          sponsorName: listForm.sponsorName.trim(),
          eventDate: listForm.eventDate,
          expiryDate: expiryTimestamp,
          guestEntries: parsedEntries,
          isArchived: false,
          createdAt: Timestamp.now()
        });
        toast.success("Event List created successfully!");
      }

      setShowCreateModal(false);
      setEditingListId(null);
      setListForm({
        listName: '',
        sponsorName: '',
        eventDate: new Date().toISOString().split('T')[0],
        rawNames: ''
      });
      await fetchGuestLists();
    } catch (err) {
      console.error("Error saving guest list:", err);
      toast.error("Failed to save Event List");
    } finally {
      setSavingList(false);
    }
  };

  const handleToggleArchive = async (listId, archiveStatus) => {
    const actionText = archiveStatus ? "archive" : "unarchive and reset check-ins for";
    if (!window.confirm(`Are you sure you want to ${actionText} this Event List?`)) return;

    try {
      const listRef = doc(db, "guest_lists", listId);
      const targetList = guestLists.find(l => l.id === listId);

      const payload = {
        isArchived: archiveStatus,
        archivedAt: archiveStatus ? Timestamp.now() : null
      };

      if (!archiveStatus && targetList && targetList.guestEntries) {
        payload.guestEntries = targetList.guestEntries.map(entry => ({
          ...entry,
          isVerified: false,
          verifiedBy: null,
          checkedInAt: null
        }));
      }

      await updateDoc(listRef, payload);
      toast.success(`Event List ${archiveStatus ? 'archived' : 'unarchived & reset'}!`);
      await fetchGuestLists();
    } catch (err) {
      console.error(`Error trying to ${actionText} list:`, err);
      toast.error(`Could not update Event List archive state`);
    }
  };

  const handleDeleteList = async (listId) => {
    if (!window.confirm("Are you sure you want to delete this entire Event List?")) return;
    try {
      await deleteDoc(doc(db, "guest_lists", listId));
      toast.info("Event List deleted");
      if (selectedGuestList?.id === listId) setSelectedGuestList(null);
      await fetchGuestLists();
    } catch (err) {
      console.error("Error deleting guest list:", err);
      toast.error("Could not delete Event List");
    }
  };

  const handleAddSingleGuest = async () => {
    const name = singleGuestInput.trim();
    if (!name || !selectedGuestList) return;

    try {
      const listRef = doc(db, "guest_lists", selectedGuestList.id);
      const newEntry = {
        id: `entry_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
        fullName: name,
        isVerified: false,
        verifiedBy: null,
        checkedInAt: null
      };
      const updatedEntries = [...(selectedGuestList.guestEntries || []), newEntry];

      await updateDoc(listRef, { guestEntries: updatedEntries });

      setSelectedGuestList(prev => prev ? { ...prev, guestEntries: updatedEntries } : prev);
      setGuestLists(prev => prev.map(l => l.id === selectedGuestList.id ? { ...l, guestEntries: updatedEntries } : l));

      setSingleGuestInput('');
      toast.success(`Added ${formatDisplayValue(name)} to list`);
      await fetchGuestLists();
    } catch (err) {
      console.error("Error adding guest to list:", err);
      toast.error("Failed to add guest to list");
    }
  };

  const handleRemoveGuest = async (entryId) => {
    if (!selectedGuestList) return;
    try {
      const listRef = doc(db, "guest_lists", selectedGuestList.id);
      const updatedEntries = (selectedGuestList.guestEntries || []).filter(e => e.id !== entryId);
      
      await updateDoc(listRef, { guestEntries: updatedEntries });

      setSelectedGuestList(prev => prev ? { ...prev, guestEntries: updatedEntries } : prev);
      setGuestLists(prev => prev.map(l => l.id === selectedGuestList.id ? { ...l, guestEntries: updatedEntries } : l));

      toast.info("Guest removed from list");
      await fetchGuestLists();
    } catch (err) {
      console.error("Error removing guest from list:", err);
      toast.error("Failed to remove guest");
    }
  };

  const handleCheckInGuest = async (guestEntry) => {
    if (!selectedGuestList || guestEntry.isVerified) return;

    try {
      const checkResult = await verifyMemberProfile(guestEntry.fullName);

      if (checkResult) {
        const isBanned = !!checkResult.isBanned;
        const isFlagged = !!checkResult.flagged;
        const hasIncident = !!checkResult.incident;
        const isExpired = !!checkResult.expired;

        if (isBanned || isFlagged || hasIncident || isExpired) {
          let reasonText = isBanned ? `BANNED: ${checkResult.banReason || 'Global Ban List Match'}` :
            isFlagged ? "Flagged Account Profile" :
            hasIncident ? "Open Incident Record" : `Expired Membership: ${checkResult.expiryDate}`;

          setDenialModal({
            show: true,
            guestEntry,
            reason: reasonText,
            isBanned,
            isFlagged,
            hasIncident,
            isExpired
          });
          return;
        }
      }

      await commitEventGuestCheckIn(guestEntry, selectedGuestList);
    } catch (err) {
      console.error("Error checking in guest from list:", err);
      toast.error("Failed to check in guest from list");
    }
  };

  const commitEventGuestCheckIn = async (guestEntry, listObj, bypassReason = "") => {
    await logVisit(
      guestEntry.fullName, 
      bypassReason ? 'Master Guest List Override' : 'Master Guest List', 
      'Guest', 
      listObj.sponsorName, 
      listObj.sponsorName, 
      false, 
      false, 
      false, 
      false, 
      bypassReason, 
      listObj.listName
    );

    const listRef = doc(db, "guest_lists", listObj.id);
    const verifierTag = bypassReason ? getStaffVerifierTag(`Override: ${bypassReason}`) : getStaffVerifierTag("Master List Check-In");

    const updatedEntries = (listObj.guestEntries || []).map(e => {
      if (e.id === guestEntry.id) {
        return {
          ...e,
          isVerified: true,
          verifiedBy: verifierTag,
          checkedInAt: Timestamp.now()
        };
      }
      return e;
    });

    await updateDoc(listRef, { guestEntries: updatedEntries });

    setSelectedGuestList(prev => prev && prev.id === listObj.id ? { ...prev, guestEntries: updatedEntries } : prev);
    setGuestLists(prev => prev.map(l => l.id === listObj.id ? { ...l, guestEntries: updatedEntries } : l));

    toast.success(`${formatDisplayValue(guestEntry.fullName)} checked in and verified!`);
    if (onCheckInSuccess) onCheckInSuccess();
    await fetchGuestLists();
  };

  const handleAuthorizeEventBypass = async () => {
    if (!denialModal.guestEntry) return;

    const code = window.prompt("SECURITY OVERRIDE: Enter Admin Passcode to continue:");
    if (code !== ADMIN_PASSCODE) return toast.error("Unauthorized: Invalid Passcode");

    const reason = window.prompt("Passcode Accepted. Enter reason for bypass (REQUIRED):");
    if (!reason || reason.trim().length < 3) return toast.warn("Bypass cancelled: Reason is required.");

    try {
      await commitEventGuestCheckIn(denialModal.guestEntry, selectedGuestList, reason.trim());
      setDenialModal({ show: false, guestEntry: null, reason: '', isBanned: false, isFlagged: false, hasIncident: false, isExpired: false });
    } catch (err) {
      console.error("Error committing bypass:", err);
      toast.error("Failed to commit bypass.");
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} centered size="lg" contentClassName="qr-modal-content border border-secondary shadow-lg" backdropClassName='bg-dark'>
        <div className="qr-modal-success-bar" style={{ background: 'var(--brand-primary, #198754)' }}></div>
        <Modal.Header closeButton className="border-secondary text-white py-2 px-3">
          <Modal.Title className="fw-bold text-uppercase d-flex align-items-center" style={{ fontSize: '1.05rem', letterSpacing: '0.5px' }}>
            <FaClipboardList className="me-2 text-info" /> Shift Event Lists
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-3 text-white text-start">
          
          <div className="d-flex flex-wrap gap-1 mb-3 justify-content-start">
            <button
              type="button"
              className={`btn btn-sm flex-fill font-11 rounded-1 ${tabMode === 'active' ? 'btn-info text-white fw-bold shadow-sm' : 'btn-outline-secondary text-white-50'}`}
              onClick={() => setTabMode('active')}
            >
              Active Lists
            </button>

            {isAdmin && (
              <button
                type="button"
                className={`btn btn-sm flex-fill font-11 rounded-1 ${tabMode === 'admin' ? 'btn-info text-white fw-bold shadow-sm' : 'btn-outline-secondary text-white-50'}`}
                onClick={() => setTabMode('admin')}
              >
                Admin List
              </button>
            )}

            <button
              type="button"
              className={`btn btn-sm flex-fill font-11 rounded-1 ${tabMode === 'archived' ? 'btn-info text-white fw-bold shadow-sm' : 'btn-outline-secondary text-white-50'}`}
              onClick={() => setTabMode('archived')}
            >
              Archived Lists
            </button>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div className="d-flex flex-wrap gap-2 align-items-center custom-scroll overflow-x-auto pb-1" style={{ maxWidth: '78%' }}>
              {filteredGuestLists.map(list => {
                const isSelected = selectedGuestList?.id === list.id;
                return (
                  <button
                    key={list.id}
                    className={`btn btn-xs py-1 px-3 text-nowrap rounded-pill font-11 transition-all ${
                      isSelected 
                        ? 'btn-info text-white fw-bold shadow-sm border-info' 
                        : 'btn-outline-light text-white fw-semibold'
                    }`}
                    style={{ 
                      fontSize: '0.74rem', 
                      backgroundColor: isSelected ? undefined : 'rgba(255, 255, 255, 0.18)',
                      color: '#ffffff',
                      borderColor: isSelected ? undefined : 'rgba(255, 255, 255, 0.4)'
                    }}
                    onClick={() => setSelectedGuestList(list)}
                  >
                    {list.listName} <span style={{ color: isSelected ? '#ffffff' : '#d0d0d0', fontSize: '0.68rem' }}>({list.eventDate})</span> {list.isArchived ? '[Archived]' : ''}
                  </button>
                );
              })}
              {filteredGuestLists.length === 0 && (
                <span className="qr-subtext small opacity-75 italic" style={{ fontSize: '0.75rem' }}>
                  No {tabMode === 'admin' ? 'admin' : tabMode} event lists found.
                </span>
              )}
            </div>

            {isAdmin && tabMode === 'admin' && (
              <button
                className="qr-btn qr-btn-primary py-1 px-2.5 d-flex align-items-center ms-auto rounded-1"
                style={{ fontSize: '0.75rem', height: '28px' }}
                onClick={() => {
                  setEditingListId(null);
                  setListForm({
                    listName: '',
                    sponsorName: '',
                    eventDate: new Date().toISOString().split('T')[0],
                    rawNames: ''
                  });
                  setShowCreateModal(true);
                }}
              >
                <FaPlus className="me-1" size={10} /> NEW LIST
              </button>
            )}
          </div>

          {selectedGuestList ? (
            <div className="p-3 rounded border border-secondary" style={{ background: 'rgba(18, 18, 18, 0.7)' }}>
              <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                <div>
                  <h6 className="fw-bold m-0 text-white d-flex align-items-center gap-2" style={{ fontSize: '0.95rem' }}>
                    {selectedGuestList.listName}
                    {selectedGuestList.isArchived && <span className="badge bg-warning text-dark font-10 py-0.5 px-1.5 rounded-pill" style={{ fontSize: '0.6rem' }}>ARCHIVED</span>}
                  </h6>
                  <p className="qr-subtext small m-0 text-white-50 mt-0.5" style={{ fontSize: '0.72rem' }}>
                    Sponsor: <strong className="text-white">{formatDisplayValue(selectedGuestList.sponsorName)}</strong> | Shift Date: <strong className="text-white">{selectedGuestList.eventDate}</strong>
                  </p>
                </div>

                <div className="d-flex gap-2 align-items-center">
                  {selectedGuestList.isArchived ? (
                    <>
                      <button
                        className="btn btn-xs btn-info text-white font-10 py-1 px-2.5 d-flex align-items-center fw-semibold shadow-sm rounded-1 me-1.5"
                        style={{ fontSize: '0.7rem' }}
                        title="Unarchive and Reset List Check-ins"
                        onClick={() => handleToggleArchive(selectedGuestList.id, false)}
                      >
                        <FaUndo className="me-1" size={10} /> Unarchive & Reset
                      </button>
                      {isAdmin && (
                        <button
                          className="btn btn-xs btn-danger text-white font-10 py-1 px-2.5 d-flex align-items-center fw-semibold shadow-sm rounded-1"
                          style={{ fontSize: '0.7rem' }}
                          title="Delete List"
                          onClick={() => handleDeleteList(selectedGuestList.id)}
                        >
                          <FaTrash className="me-1" size={10} /> Delete
                        </button>
                      )}
                    </>
                  ) : (
                    isAdmin && tabMode === 'admin' && (
                      <>
                        <button
                          className="btn btn-xs btn-success text-white font-10 py-1 px-2.5 d-flex align-items-center fw-semibold shadow-sm rounded-1 me-1"
                          style={{ fontSize: '0.7rem' }}
                          title="Edit List"
                          onClick={() => {
                            setEditingListId(selectedGuestList.id);
                            const existingNames = (selectedGuestList.guestEntries || []).map(e => e.fullName).join('\n');
                            setListForm({
                              listName: selectedGuestList.listName || '',
                              sponsorName: selectedGuestList.sponsorName || '',
                              eventDate: selectedGuestList.eventDate || new Date().toISOString().split('T')[0],
                              rawNames: existingNames
                            });
                            setShowCreateModal(true);
                          }}
                        >
                          <FaEdit className="me-1" size={10} /> Edit
                        </button>
                        <button
                          className="btn btn-xs btn-warning text-dark font-10 py-1 px-2.5 d-flex align-items-center fw-semibold shadow-sm rounded-1 me-1"
                          style={{ fontSize: '0.7rem' }}
                          title="Archive List"
                          onClick={() => handleToggleArchive(selectedGuestList.id, true)}
                        >
                          <FaArchive className="me-1" size={10} /> Archive
                        </button>
                        <button
                          className="btn btn-xs btn-danger text-white font-10 py-1 px-2.5 d-flex align-items-center fw-semibold shadow-sm rounded-1"
                          style={{ fontSize: '0.7rem' }}
                          title="Delete List"
                          onClick={() => handleDeleteList(selectedGuestList.id)}
                        >
                          <FaTrash className="me-1" size={10} /> Delete
                        </button>
                      </>
                    )
                  )}
                </div>
              </div>

              {!selectedGuestList.isArchived && (
                <InputGroup className="glass-input my-2.5">
                  <Form.Control
                    placeholder="Add guest name to this list..."
                    className="qr-input py-1 px-2"
                    style={{ fontSize: '0.78rem' }}
                    value={singleGuestInput}
                    onChange={(e) => setSingleGuestInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddSingleGuest()}
                  />
                  <button className="qr-btn qr-btn-primary px-3 py-1" style={{ fontSize: '0.72rem' }} onClick={handleAddSingleGuest}>
                    <FaPlus size={10} className="me-1" /> Add
                  </button>
                </InputGroup>
              )}

              <div className="qr-card-list custom-scroll mt-2" style={{ maxHeight: '340px', overflowY: 'auto' }}>
                {selectedGuestList.guestEntries && selectedGuestList.guestEntries.length > 0 ? (
                  selectedGuestList.guestEntries.map((guest) => (
                    <div
                      key={guest.id}
                      className="qr-log-card py-2 px-2.5 d-flex flex-column mb-2 border border-secondary rounded-2"
                      style={{ 
                        background: guest.isVerified ? 'rgba(25, 135, 84, 0.12)' : 'rgba(20, 20, 20, 0.65)',
                        borderColor: guest.isVerified ? 'rgba(25, 135, 84, 0.35)' : 'rgba(255, 255, 255, 0.08)'
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between w-100 mb-1">
                        <div className="d-flex align-items-center gap-2">
                          <span className="fw-bold text-white" style={{ fontSize: '0.82rem' }}>
                            {formatDisplayValue(guest.fullName)}
                          </span>
                          {guest.isVerified ? (
                            <FaCheckCircle className="text-success" title={`Verified by ${guest.verifiedBy || 'Staff'}`} size={14} />
                          ) : (
                            <FaTimesCircle className="text-danger" title="Unverified Guest" size={14} />
                          )}
                        </div>
                        {guest.isVerified && (
                          <span className="badge bg-success text-white font-10 py-0.5 px-1.5" style={{ fontSize: '0.6rem' }}>CHECKED IN</span>
                        )}
                      </div>

                      <div className="entry-footer-bar border-top border-secondary pt-1.5 mt-1 d-flex align-items-center justify-content-between gap-1 w-100">
                        <div className="d-flex align-items-center gap-1.5">
                          {!guest.isVerified && !selectedGuestList.isArchived ? (
                            <button
                              className="btn btn-xs btn-success text-white fw-bold d-flex align-items-center py-1 px-2 shadow-sm rounded-1"
                              style={{ fontSize: '0.68rem', lineHeight: '1' }}
                              onClick={() => handleCheckInGuest(guest)}
                            >
                              <FaCheckCircle className="me-1" size={9} /> Verify & Check In
                            </button>
                          ) : guest.isVerified && guest.verifiedBy ? (
                            <span className="qr-subtext-small text-white-50" style={{ fontSize: '0.62rem' }}>
                              Verified by: {guest.verifiedBy}
                            </span>
                          ) : null}
                        </div>

                        <button
                          className="btn btn-xs btn-danger text-white d-flex align-items-center py-0.5 px-2 ms-auto rounded-1 fw-semibold shadow-sm"
                          style={{ fontSize: '0.64rem', lineHeight: '1' }}
                          title="Remove Guest"
                          onClick={() => handleRemoveGuest(guest.id)}
                        >
                          <FaTrash className="me-1" size={8} /> Remove
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4 qr-subtext opacity-50 small">No guests on this list yet.</div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-5 qr-subtext opacity-90 small">
              Select an event list above to view guest entries.
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="border-secondary py-1.5 px-3">
          <button className="qr-btn qr-btn-warning w-100 py-1.5" style={{ fontSize: '0.82rem' }} onClick={onHide}>
            Close Event Manager
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={denialModal.show} onHide={() => setDenialModal({ show: false, guestEntry: null, reason: '', isBanned: false, isFlagged: false, hasIncident: false, isExpired: false })} centered contentClassName="qr-modal-content border border-danger shadow-lg" backdropClassName='bg-dark'>
        <div className="qr-modal-success-bar" style={{ background: 'var(--brand-secondary, #dc3545)' }}></div>
        <Modal.Body className="p-4 text-center text-white">
          <FaExclamationTriangle size={36} className="text-danger mb-2" />
          <h4 className="fw-bold m-0 text-uppercase">Check-In Denied</h4>
          <p className="qr-subtext small opacity-50 mb-3">{denialModal.guestEntry ? formatDisplayValue(denialModal.guestEntry.fullName) : ''}</p>

          <div className="p-3 mb-3 rounded border border-danger" style={{ background: 'rgba(220,53,69,0.1)' }}>
            <p className="small text-danger fw-bold m-0">{denialModal.reason}</p>
          </div>

          <div className="mt-3 p-3 rounded border border-secondary" style={{ background: 'rgba(255,255,255,0.03)' }}>
            <p className="small text-white-50 mb-2">Security override required?</p>
            <button className="qr-btn qr-btn-primary w-100 py-2 d-flex align-items-center justify-content-center" onClick={handleAuthorizeEventBypass}>
              <FaShieldAlt className="me-2" /> AUTHORIZE OVERRIDE
            </button>
          </div>

          <button className="qr-btn qr-btn-warning w-100 mt-3" onClick={() => setDenialModal({ show: false, guestEntry: null, reason: '', isBanned: false, isFlagged: false, hasIncident: false, isExpired: false })}>
            Cancel & Block
          </button>
        </Modal.Body>
      </Modal>

      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered contentClassName="qr-modal-content border border-secondary shadow-lg" backdropClassName='bg-dark'>
        <div className="qr-modal-success-bar" style={{ background: 'var(--brand-primary, #198754)' }}></div>
        <Modal.Body className="p-4 text-white text-start">
          <div className="text-center mb-3">
            <h5 className="fw-bold m-0 text-uppercase">{editingListId ? "Edit Event List" : "Create Master Event List"}</h5>
            <p className="qr-subtext small m-0 text-white-50 mt-1" style={{ fontSize: '0.7rem' }}>Shift Validity: Date until 2:00 AM Next Morning</p>
          </div>

          <Form.Group className="mb-2">
            <Form.Label className="qr-label mb-1" style={{ fontSize: '0.7rem' }}>LIST / EVENT NAME</Form.Label>
            <Form.Control
              type="text"
              className="glass-input py-1 px-2"
              style={{ fontSize: '0.8rem' }}
              placeholder="e.g., Wedding, VIP Pop-Up"
              value={listForm.listName}
              onChange={(e) => setListForm({ ...listForm, listName: e.target.value })}
            />
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label className="qr-label mb-1" style={{ fontSize: '0.7rem' }}>SPONSOR MEMBER NAME / EMAIL</Form.Label>
            <Form.Control
              type="text"
              className="glass-input py-1 px-2"
              style={{ fontSize: '0.8rem' }}
              placeholder="e.g., Shaun Gay"
              value={listForm.sponsorName}
              onChange={(e) => setListForm({ ...listForm, sponsorName: e.target.value })}
            />
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label className="qr-label mb-1" style={{ fontSize: '0.7rem' }}>SHIFT DATE</Form.Label>
            <Form.Control
              type="date"
              className="glass-input py-1 px-2"
              style={{ fontSize: '0.8rem' }}
              value={listForm.eventDate}
              onChange={(e) => setListForm({ ...listForm, eventDate: e.target.value })}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="qr-label mb-1" style={{ fontSize: '0.7rem' }}>PASTE GUEST NAMES (ONE PER LINE)</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              className="glass-input p-2"
              style={{ fontSize: '0.8rem' }}
              placeholder="John Doe&#10;Jane Smith&#10;Jack Black"
              value={listForm.rawNames}
              onChange={(e) => setListForm({ ...listForm, rawNames: e.target.value })}
            />
          </Form.Group>

          <button
            className="qr-btn qr-btn-primary w-100 d-flex align-items-center justify-content-center mb-2 py-2"
            style={{ fontSize: '0.8rem' }}
            onClick={handleSaveGuestList}
            disabled={savingList}
          >
            {savingList ? <Spinner size="sm" className="me-2" /> : <FaClipboardList className="me-2" size={12} />}
            {editingListId ? "SAVE LIST CHANGES" : "CREATE & COMMIT MASTER LIST"}
          </button>
          <button className="qr-btn qr-btn-warning w-100 py-1.5" style={{ fontSize: '0.8rem' }} onClick={() => setShowCreateModal(false)}>
            Cancel
          </button>
        </Modal.Body>
      </Modal>
    </>
  );
});

export default EventListManager;